import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { AppHttpService } from './app-http.service';
import { Observable } from 'rxjs';
import { Organization, OrganizationType, Account, Invitation } from '../models';
import { OrgSettingValue } from '../helpers/org-settings';

export interface OrganizationSettings {
  values: Record<string, OrgSettingValue>;
  editable: string[];
}
import {
  ArcgisConnection, ArcgisConnectionInput, ArcgisConnectionSaveResponse, ArcgisConnectionTest,
  ArcgisLayerUrls, ArcgisPreflight,
} from '../models/arcgis-push';

@Injectable({ providedIn: 'root' })
export class OrganizationService {

  constructor(
    private appHttpService: AppHttpService
  ) {}

  getRecord(id: string, qp = {}) {
    return this.appHttpService.getService(`/v1/organization/${id}`, qp).pipe(
      map((resp: any) => {
        if (resp.data) {
          return new Organization(resp.data);
        }
        throw new Error('Error parsing response');
      })
    );
  }

  /**
   * Fetch OrganizationTypes
   */
  getTypes() {
    return this.appHttpService.getService('/v1/organization/types').pipe(
      map((resp: any) => {
        if (resp.data && Array.isArray(resp.data)) {
          return resp.data.map(o => new OrganizationType(o));
        }
        throw new Error('Error parsing response');
      })
    );
  }

  /**
   * Returns list of Records
   * @param pagination start
   * @param filters query params
   */
  getRecords(pagination, filters = []) {
    const start = pagination.pageIndex * pagination.pageSize;
    const qp = { start, total: pagination.pageSize, include: ['type', 'createdBy', 'updatedBy']};
    return this.appHttpService.getService('/v1/organization', qp).pipe(
      map((resp: any) => {
        if (resp.data && Array.isArray(resp.data)) {
          const result = resp.data.map(r => new Organization(r));
          // e.g. total records, type etc.,
          const meta = Object.assign(resp.meta || {}, { type: resp.type });
          return { result, meta };
        }
        throw new Error('Error parsing response');
      })
    );
  }

  /**
   * Creates a new organization
   * @param input Organization
   */
  create(input) {
    return this.appHttpService.postService('/v1/organization', input).pipe(
      map((resp: any) => {
        if (resp.data) {
          return new Organization(resp.data);
        }
        throw new Error('Error parsing response');
      })
    );
  }

  /**
   * The organization Settings tab: the settings the caller may see (`values`) and change (`editable`).
   * @param id Organization.id
   */
  getSettings(id: string): Observable<OrganizationSettings> {
    return this.appHttpService.getService(`/v1/organization/${id}/settings`).pipe(map((resp: any) => resp.data));
  }

  /**
   * Changes some organization settings; the response is the same shape as getSettings.
   * @param id Organization.id
   * @param patch setting key → value (null resets an org_config setting to the app default)
   */
  updateSettings(id: string, patch: Record<string, OrgSettingValue>): Observable<OrganizationSettings> {
    return this.appHttpService.patchService(`/v1/organization/${id}/settings`, patch).pipe(map((resp: any) => resp.data));
  }

  /**
   * Updates Organization
   * @param body Organization
   */
  update(id, body) {
    return this.appHttpService.putService(`/v1/organization/${id}`, body).pipe(
      map((resp: any) => {
        if (resp.data) {
          return new Organization(resp.data);
        }
        throw new Error('Error parsing response');
      })
    );
  }

  /**
   * Deletes Organization
   * @param organization Organization
   */
  delete(organization: Organization) {
    // Todo check acl here
    return this.appHttpService.deleteService(`/v1/organization/${organization.id}`);
  }

  /**
   * Fetches Organization accounts
   * @param organization
   * @param pagination
   * @param qp
   */
  getAccounts(organization: Organization, pagination, qp: any = {}) {
    qp.start = pagination.pageIndex * pagination.pageSize;
    qp.total = pagination.pageSize;
    const path = `/v1/organization/${organization.id}/accounts`;
    return this.appHttpService.getService(path, qp).pipe(
      map((resp: any) => {
        if (resp.data && Array.isArray(resp.data)) {
          const result = resp.data.map(r => new Account(r));
          const meta = Object.assign(resp.meta || {}, { type: resp.type });
          return { result, meta };
        }
        throw new Error('Error parsing response');
      })
    );
  }

  /**
   * Fetches Organization invites
   * @param organization
   * @param pagination
   * @param qp
   */
  getInvites(organization: Organization, pagination, qp: any = {}) {
    qp.start = pagination.pageIndex * pagination.pageSize;
    qp.total = pagination.pageSize;
    qp.organization_id = organization.id;
    const path = `/v1/invite/search`;
    return this.appHttpService.getService(path, qp).pipe(
      map((resp: any) => {
        if (resp.data && Array.isArray(resp.data)) {
          const result = resp.data.map(r => new Invitation(r));
          const meta = Object.assign(resp.meta || {}, { type: resp.type });
          return { result, meta };
        }
        throw new Error('Error parsing response');
      })
    );
  }

  /**
   * Invite a user to organization
   * @param organization Organization
   * @param invitee email|phone
   * @param role Role.id
   * @param qp query-params
   */
  inviteUser(organization: Organization, invitee: string, role: string, qp: any = {}) {
    const payload = {
      invitee,
      invitee_role: role,
      invite_type: 'organization',
      invite_to: organization.id,
    };

    return this.appHttpService.postService('/v1/invite/account', payload).pipe(
      map((resp: any) => {
        if (resp.data) {
          return new Invitation(resp.data);
        }
        throw new Error('Error parsing response');
      })
    );
  }

  getAvailableMembers(orgId: string) {
    return this.appHttpService.getService(`/v1/organization/${orgId}/available-members`).pipe(
      map((resp: any) => {
        if (resp.data && Array.isArray(resp.data)) {
          return resp.data;
        }
        throw new Error('Error parsing response');
      })
    );
  }

  addMember(orgId: string, body: any) {
    return this.appHttpService.postService(`/v1/organization/${orgId}/member`, body);
  }

  updateMember(orgId: string, accountId: string, body: any) {
    return this.appHttpService.putService(`/v1/organization/${orgId}/member/${accountId}`, body);
  }

  removeMember(orgId: string, accountId: string) {
    return this.appHttpService.deleteService(`/v1/organization/${orgId}/member/${accountId}`);
  }

  // ArcGIS credential for the field-record push. The secret is write-only:
  // GET returns has_secret, and a PUT without secret keeps the stored one.
  getArcgisConnection(orgId: string): Observable<ArcgisConnection> {
    return this.appHttpService.getService(`/v2/organization/${orgId}/arcgis-connection`);
  }

  saveArcgisConnection(orgId: string, body: ArcgisConnectionInput): Observable<ArcgisConnectionSaveResponse> {
    return this.appHttpService.putService(`/v2/organization/${orgId}/arcgis-connection`, body);
  }

  testArcgisConnection(orgId: string): Observable<ArcgisConnectionTest> {
    return this.appHttpService.postService(`/v2/organization/${orgId}/arcgis-connection/test`, {});
  }

  testArcgisLayers(orgId: string, body: Partial<ArcgisLayerUrls>): Observable<ArcgisPreflight> {
    return this.appHttpService.postService(`/v2/organization/${orgId}/arcgis-connection/test-layers`, body);
  }

  checkEmailExists(email: string) {
    return this.appHttpService.getService(`/v1/account/check`, { email });
  }

  checkPhoneExists(phone: string) {
    return this.appHttpService.getService(`/v1/account/check`, { phone });
  }

  /**
   * Fetch organization roles
   */
  getOrgRoles() {
    return this.appHttpService.getService('/v1/organization/roles').pipe(
      map((resp: any) => {
        if (resp.data && Array.isArray(resp.data)) {
          return resp.data;
        }
        throw new Error('Error parsing response');
      })
    );
  }
}
